import { useState } from "react";
import { gql, useApolloClient, useReactiveVar } from "@apollo/client";
import {
  Box,
  Button,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Chip,
} from "@mui/material";
import { userEntityRelationVar } from "../../../client/cache";
import analyzeSupplyDemand from "../../../function/utilityAPI/analyzeSupplyDemand";
import { toChinaDateString } from "../../../lib/chinaDate";


const listCancelledSPOs = /* GraphQL */ `
  query POByDefinerID(
    $definerID: ID!
    $openQty: ModelFloatKeyConditionInput
    $filter: ModelPOFilterInput
    $limit: Int
    $nextToken: String
  ) {
    POByDefinerID(
      definerID: $definerID
      openQty: $openQty
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        openQty
        statusCode
      }
      nextToken
    }
  }
`;

const updatePOOpenQty = /* GraphQL */ `
  mutation UpdatePO($input: UpdatePOInput!) {
    updatePO(input: $input) {
      id
      openQty
      statusCode
    }
  }
`;

async function fixCancelledSPOs({ client, definerID, entityIDs }) {
  const filter = {
    and: [
      { or: entityIDs.map((id) => ({ customerID: { eq: id } })) },
      { statusCode: { eq: "400" } },
    ],
  };

  const all = [];
  let nextToken = null;
  do {
    const { data } = await client.query({
      query: gql(listCancelledSPOs),
      variables: { definerID, openQty: { gt: 0 }, filter, limit: 1000, nextToken },
      fetchPolicy: "network-only",
    });
    all.push(...(data?.POByDefinerID?.items || []));
    nextToken = data?.POByDefinerID?.nextToken;
  } while (nextToken);

  if (all.length === 0) return 0;

  await Promise.all(
    all.map((po) =>
      client.mutate({
        mutation: gql(updatePOOpenQty),
        variables: { input: { id: po.id, openQty: 0 } },
      })
    )
  );

  return all.length;
}

const STATUS_COLOR = {
  under: "#ffebee",
  over: "#fff8e1",
  balanced: "#e8f5e9",
};

const STATUS_CHIP = {
  under: { label: "Under", color: "error" },
  over: { label: "Over", color: "warning" },
  balanced: { label: "Balanced", color: "success" },
};

const SUB_ROW_COLOR = {
  under: "#fce4ec",
  over: "#fff3e0",
  balanced: "#dcedc8",
};

function fmt(n) {
  return (n > 0 ? "+" : "") + n.toLocaleString();
}

function exportCSV(rows) {
  const headers = [
    "Material Code",
    "MPN",
    "Manufacturer",
    "CPO Demand (open)",
    "SPO In-Transit (open)",
    "Inventory",
    "Total Supply",
    "Balance",
    "Status",
    "Type",
  ];

  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };

  const lines = [headers.join(",")];

  for (const r of rows) {
    // Combined row
    lines.push(
      [
        r.code,
        r.materials.length === 1 ? r.materials[0].mpn : `(${r.materials.length} items)`,
        r.materials.length === 1 ? r.materials[0].mfr : "",
        r.cpoDemand,
        r.spoSupply,
        r.inventory,
        r.totalSupply,
        r.balance,
        r.status,
        "combined",
      ]
        .map(esc)
        .join(",")
    );
    // Sub-rows for multi-material codes
    if (r.materials.length > 1) {
      for (const m of r.materials) {
        lines.push(
          [
            "",
            m.mpn,
            m.mfr,
            m.cpoDemand,
            m.spoSupply,
            m.inventory,
            m.totalSupply,
            m.balance,
            "",
            "item",
          ]
            .map(esc)
            .join(",")
        );
      }
    }
  }

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `supply_demand_${toChinaDateString()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function SupplyDemandAnalysis() {
  const client = useApolloClient();
  const { entityID, subsidiaryIDs = [] } = useReactiveVar(userEntityRelationVar);

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [fixing, setFixing] = useState(false);
  const [fixResult, setFixResult] = useState(null);

  const handleRun = async () => {
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const data = await analyzeSupplyDemand({
        client,
        definerID: entityID,
        entityIDs: [entityID, ...subsidiaryIDs],
      });
      setResults(data);
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const displayed = results
    ? filter === "all"
      ? results
      : results.filter((r) => r.status === filter)
    : [];

  const counts = results
    ? {
        all: results.length,
        under: results.filter((r) => r.status === "under").length,
        over: results.filter((r) => r.status === "over").length,
        balanced: results.filter((r) => r.status === "balanced").length,
      }
    : null;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <Stack spacing={3}>
        <Typography variant="h4">Supply vs Demand Analysis</Typography>
        <Typography variant="body2" color="text.secondary">
          Compares open SPO quantities (in-transit supply) plus current
          inventory against open CPO quantities (customer demand). Grouped by
          material code — codes with multiple approved materials show combined
          totals.
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="contained"
            onClick={handleRun}
            disabled={loading || !entityID}
          >
            {loading ? <CircularProgress size={20} /> : "Run Analysis"}
          </Button>

          {results && (
            <Button variant="outlined" onClick={() => exportCSV(displayed)}>
              Export CSV
            </Button>
          )}

          <Button
            variant="outlined"
            color="warning"
            disabled={fixing || !entityID}
            onClick={async () => {
              setFixing(true);
              setFixResult(null);
              try {
                const count = await fixCancelledSPOs({
                  client,
                  definerID: entityID,
                  entityIDs: [entityID, ...subsidiaryIDs],
                });
                setFixResult({ count, error: null });
              } catch (err) {
                setFixResult({ count: 0, error: err.message });
              } finally {
                setFixing(false);
              }
            }}
          >
            {fixing ? <CircularProgress size={20} /> : "Fix Cancelled SPO openQty"}
          </Button>
        </Stack>

        {fixResult && (
          <Alert severity={fixResult.error ? "error" : "success"}>
            {fixResult.error
              ? `Fix failed: ${fixResult.error}`
              : fixResult.count === 0
              ? "No cancelled SPOs with open qty found — already clean."
              : `Fixed ${fixResult.count} cancelled SPO line(s): openQty zeroed out.`}
          </Alert>
        )}

        {loading && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}

        {results && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Alert
                severity={counts.under > 0 ? "error" : "success"}
                sx={{ flex: 1 }}
              >
                {counts.all} codes &nbsp;|&nbsp;
                <strong>{counts.under} under-ordered</strong> &nbsp;|&nbsp;
                {counts.over} over-ordered &nbsp;|&nbsp;
                {counts.balanced} balanced
              </Alert>
            </Stack>

            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={(_, v) => v && setFilter(v)}
              size="small"
            >
              <ToggleButton value="all">All ({counts.all})</ToggleButton>
              <ToggleButton value="under" sx={{ color: "error.main" }}>
                Under ({counts.under})
              </ToggleButton>
              <ToggleButton value="over" sx={{ color: "warning.main" }}>
                Over ({counts.over})
              </ToggleButton>
              <ToggleButton value="balanced" sx={{ color: "success.main" }}>
                Balanced ({counts.balanced})
              </ToggleButton>
            </ToggleButtonGroup>

            <Paper>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Material Code</TableCell>
                      <TableCell>MPN</TableCell>
                      <TableCell>Manufacturer</TableCell>
                      <TableCell align="right">CPO Demand</TableCell>
                      <TableCell align="right">SPO In-Transit</TableCell>
                      <TableCell align="right">Inventory</TableCell>
                      <TableCell align="right">Total Supply</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayed.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          No materials found
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayed.map((row) => {
                        const isMulti = row.materials.length > 1;
                        const singleMat = !isMulti ? row.materials[0] : null;
                        return (
                          <TableRowGroup
                            key={row.code || row.materials[0]?.materialID}
                            row={row}
                            isMulti={isMulti}
                            singleMat={singleMat}
                          />
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

function TableRowGroup({ row, isMulti, singleMat }) {
  return (
    <>
      {/* Combined / single row */}
      <TableRow sx={{ bgcolor: STATUS_COLOR[row.status] }}>
        <TableCell sx={{ fontWeight: isMulti ? "bold" : "normal" }}>
          {row.code}
        </TableCell>
        <TableCell>
          {isMulti ? (
            <Typography variant="caption" color="text.secondary">
              {row.materials.length} items ↓
            </Typography>
          ) : (
            singleMat?.mpn
          )}
        </TableCell>
        <TableCell>{isMulti ? "" : singleMat?.mfr}</TableCell>
        <TableCell align="right">{row.cpoDemand.toLocaleString()}</TableCell>
        <TableCell align="right">{row.spoSupply.toLocaleString()}</TableCell>
        <TableCell align="right">{row.inventory.toLocaleString()}</TableCell>
        <TableCell align="right">{row.totalSupply.toLocaleString()}</TableCell>
        <TableCell align="right" sx={{ fontWeight: "bold" }}>
          {fmt(row.balance)}
        </TableCell>
        <TableCell align="center">
          <Chip
            size="small"
            label={STATUS_CHIP[row.status].label}
            color={STATUS_CHIP[row.status].color}
          />
        </TableCell>
      </TableRow>

      {/* Sub-rows for each material in a multi-material code */}
      {isMulti &&
        row.materials.map((m) => (
          <TableRow
            key={m.materialID}
            sx={{ bgcolor: SUB_ROW_COLOR[row.status] }}
          >
            <TableCell sx={{ pl: 4, borderBottom: "none" }} />
            <TableCell sx={{ pl: 4 }}>{m.mpn}</TableCell>
            <TableCell>{m.mfr}</TableCell>
            <TableCell align="right">{m.cpoDemand.toLocaleString()}</TableCell>
            <TableCell align="right">{m.spoSupply.toLocaleString()}</TableCell>
            <TableCell align="right">{m.inventory.toLocaleString()}</TableCell>
            <TableCell align="right">{m.totalSupply.toLocaleString()}</TableCell>
            <TableCell align="right" sx={{ color: m.balance < 0 ? "error.main" : m.balance > 0 ? "warning.main" : "success.main" }}>
              {fmt(m.balance)}
            </TableCell>
            <TableCell />
          </TableRow>
        ))}
    </>
  );
}
